import { useEffect, useState } from "react";
import {
  DEFAULT_RTO_TERMS,
  cacheRtoTermsForOrg,
  loadRtoTermsForOrg,
  type RtoTermDefinition,
} from "@/lib/rto/rtoTermSettings";
import { getOpsSettings, patchOpsSettings, rtoTermDefinitionsFromOpsSettingsResponse } from "@/lib/opsPaperworkClient";
import { deriveRtoStates, mergeStatePayoffMap, normalizeStateCode } from "@/components/ops/rtoSettingsSectionHelpers";

export function useRtoSettingsSection(organizationId: string) {
  const [rtoTerms, setRtoTerms] = useState<RtoTermDefinition[]>(DEFAULT_RTO_TERMS);
  const [manufacturerOptions, setManufacturerOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [useManufacturerTerms, setUseManufacturerTerms] = useState(false);
  const [manufacturerSourceId, setManufacturerSourceId] = useState<string>("");
  const [openRtoActionsFor, setOpenRtoActionsFor] = useState<string | null>(null);
  const [rtoStates, setRtoStates] = useState<string[]>([]);
  const [rtoStateInput, setRtoStateInput] = useState("");
  const [showRtoStateEditor, setShowRtoStateEditor] = useState(false);
  const [openStateOverridesByTerm, setOpenStateOverridesByTerm] = useState<Record<string, boolean>>({});
  const [salesConsoleRtoTitle, setSalesConsoleRtoTitle] = useState("");
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRtoTerms(loadRtoTermsForOrg(organizationId));
    void (async () => {
      const res = await getOpsSettings();
      if (!res.ok || !res.data?.settings) return;
      const envelope = res.data as Record<string, unknown>;
      const settingsRow = res.data.settings as Record<string, unknown>;
      const options = Array.isArray(envelope.manufacturer_options)
        ? (envelope.manufacturer_options as Array<{ id: string; name: string }>)
        : [];
      setManufacturerOptions(options);
      const useManufacturer =
        typeof settingsRow.rto_use_manufacturer_terms === "boolean"
          ? settingsRow.rto_use_manufacturer_terms
          : options.length === 1;
      const manufacturerId =
        typeof settingsRow.rto_source_manufacturer_org_id === "string" && settingsRow.rto_source_manufacturer_org_id
          ? settingsRow.rto_source_manufacturer_org_id
          : options[0]?.id ?? "";
      const loadedRtoTitle =
        typeof settingsRow.sales_console_rto_title === "string" ? settingsRow.sales_console_rto_title : "";
      setUseManufacturerTerms(useManufacturer);
      setManufacturerSourceId(manufacturerId);
      setSalesConsoleRtoTitle(loadedRtoTitle);
      const parsed = rtoTermDefinitionsFromOpsSettingsResponse(envelope);
      if (!parsed.length) return;
      setRtoTerms(parsed);
      setRtoStates(deriveRtoStates(parsed));
      cacheRtoTermsForOrg(organizationId, parsed);
    })();
  }, [organizationId]);

  async function saveRtoTerms() {
    setSaving(true);
    const termsToSave = rtoTerms.map((term) => ({
      ...term,
      early_purchase_percent_by_state: mergeStatePayoffMap(term, rtoStates),
    }));
    const body: Record<string, unknown> = {
      rto_use_manufacturer_terms: useManufacturerTerms,
      rto_source_manufacturer_org_id: manufacturerSourceId || null,
    };
    if (!useManufacturerTerms) body.rto_term_definitions = termsToSave;
    const res = await patchOpsSettings(body);
    if (!res.ok) {
      setMsg({ text: `RTO term save failed: ${res.error}`, ok: false });
      setSaving(false);
      return;
    }
    if (!useManufacturerTerms) {
      cacheRtoTermsForOrg(organizationId, termsToSave);
    } else {
      const refresh = await getOpsSettings();
      if (refresh.ok) {
        const parsed = rtoTermDefinitionsFromOpsSettingsResponse(refresh.data as Record<string, unknown>);
        if (parsed.length) cacheRtoTermsForOrg(organizationId, parsed);
      }
    }
    window.dispatchEvent(new CustomEvent("rto-terms-saved", { detail: { organizationId } }));
    setMsg({ text: "RTO calculator settings saved for this organization.", ok: true });
    setSaving(false);
  }

  async function saveSalesConsoleRtoTitle() {
    setSaving(true);
    const res = await patchOpsSettings({
      sales_console_rto_title: salesConsoleRtoTitle.trim() || null,
    });
    if (!res.ok) {
      setMsg({ text: `Sales Console RTO title save failed: ${res.error}`, ok: false });
      setSaving(false);
      return;
    }
    setMsg({ text: "Sales Console RTO title saved.", ok: true });
    window.dispatchEvent(new CustomEvent("rto-terms-saved", { detail: { organizationId } }));
    setSaving(false);
  }

  function addRtoState() {
    const code = normalizeStateCode(rtoStateInput);
    setRtoStateInput("");
    if (!code) return;
    setRtoStates((prev) => {
      if (prev.includes(code)) return prev;
      const next = [...prev, code].sort();
      setRtoTerms((terms) =>
        terms.map((term) => ({
          ...term,
          early_purchase_percent_by_state: mergeStatePayoffMap(term, next),
        }))
      );
      return next;
    });
  }

  function removeRtoState(code: string) {
    setRtoStates((prev) => {
      const next = prev.filter((x) => x !== code);
      setRtoTerms((terms) =>
        terms.map((term) => {
          const cur = { ...(term.early_purchase_percent_by_state ?? {}) };
          delete cur[code];
          return { ...term, early_purchase_percent_by_state: cur };
        })
      );
      return next;
    });
  }

  return {
    rtoTerms,
    setRtoTerms,
    manufacturerOptions,
    useManufacturerTerms,
    setUseManufacturerTerms,
    manufacturerSourceId,
    setManufacturerSourceId,
    openRtoActionsFor,
    setOpenRtoActionsFor,
    rtoStates,
    setRtoStates,
    rtoStateInput,
    setRtoStateInput,
    showRtoStateEditor,
    setShowRtoStateEditor,
    openStateOverridesByTerm,
    setOpenStateOverridesByTerm,
    salesConsoleRtoTitle,
    setSalesConsoleRtoTitle,
    msg,
    saving,
    saveRtoTerms,
    saveSalesConsoleRtoTitle,
    addRtoState,
    removeRtoState,
    organizationId,
  };
}
